import { Emitter } from "@core/events/emitter";
import type { IUndoRedo } from "@models/project/IUndoRedo";
import type {
  IToolbarModel,
  SelectionMode,
  ToolId,
  TransformMode,
  TransformSpace,
} from "./IToolbarModel";

/**
 * `InMemoryToolbarModel` — placeholder implementation of
 * {@link IToolbarModel}. Tracks the active tool id and re-emits invoked
 * command names. Real per-tool behavior (selection, laser, slice, …) lands
 * in M9 with a richer `ToolsModel` that talks to the runtime + gizmos.
 */
export class InMemoryToolbarModel implements IToolbarModel {
  readonly onActiveToolChanged = new Emitter<ToolId>();
  readonly onTransformModeChanged = new Emitter<TransformMode>();
  readonly onTransformSpaceChanged = new Emitter<TransformSpace>();
  readonly onSelectionModeChanged = new Emitter<SelectionMode>();
  readonly onCommandInvoked = new Emitter<string>();

  private m_activeTool: ToolId = "select";
  private m_transformMode: TransformMode = "translate";
  private m_transformSpace: TransformSpace = "world";
  // PART, not model (user direction 2026-09-04: "Set the part selection mode to
  // be default, instead of a model selection mode as it is now for all users").
  // Nothing persists this - it is session state, so this initializer IS the
  // default every user gets on every load, and there is no stored "model" for a
  // returning user to be stuck on. It also brings the Select tool into line with
  // the Grab tool, whose own Part/Model toggle has always defaulted to Part.
  // Two surfaces change with it, both deliberately: a viewport click and hover
  // resolve to the PART under the cursor rather than remapping to its model root
  // (`resolveSelectionModeSnapshotId`), and the Measure tool's box bounds that
  // part rather than the whole assembly (`BoundingBoxToolController`).
  private m_selectionMode: SelectionMode = "part";

  constructor(private readonly m_undoRedo?: IUndoRedo) {}

  get activeTool(): ToolId {
    return this.m_activeTool;
  }

  get transformMode(): TransformMode {
    return this.m_transformMode;
  }

  get transformSpace(): TransformSpace {
    return this.m_transformSpace;
  }

  get selectionMode(): SelectionMode {
    return this.m_selectionMode;
  }

  setActiveTool(id: ToolId): void {
    if (this.m_activeTool === id) return;
    this.m_activeTool = id;
    this.onActiveToolChanged.emit(id);
  }

  setTransformMode(mode: TransformMode): void {
    if (this.m_transformMode === mode) return;
    this.m_transformMode = mode;
    this.onTransformModeChanged.emit(mode);
  }

  setTransformSpace(space: TransformSpace): void {
    if (this.m_transformSpace === space) return;
    this.m_transformSpace = space;
    this.onTransformSpaceChanged.emit(space);
  }

  setSelectionMode(mode: SelectionMode): void {
    if (this.m_selectionMode === mode) return;
    this.m_selectionMode = mode;
    this.onSelectionModeChanged.emit(mode);
  }

  invoke(command: string): void {
    if (command === "undo") this.m_undoRedo?.undo();
    else if (command === "redo") this.m_undoRedo?.redo();
    this.onCommandInvoked.emit(command);
  }
}
